import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Plus } from 'lucide-react';

import noTasksPng from '@/assets/illustrations/empty/no-tasks.png';
import noSeasonsPng from '@/assets/illustrations/empty/no-seasons.png';

import { useTaskStore } from '@/stores/task-store';
import { OperationalGuide } from '@/components/common/OperationalGuide';
import { useSeasonStore } from '@/stores/season-store';
import { usePrepActiveSeasonStore } from '@/stores/prep-active-season-store';
import { pickDefaultSeason } from '@/utils/pick-default-season';
import type { Task, TaskCategory } from '@/types/task';
import {
  filterOpenTasksForPrep,
  countPrepFilters,
  groupByCategory,
  deriveSingleStatus,
} from '@/utils/prep-filter-utils';
import { computeInitialExpandStates } from '@/utils/prep-accordion-utils';
import type { PrepFilter } from '@/utils/prep-filter-utils';
import { PrepHeader } from '@/components/tasks/PrepHeader';
import { PrepFilterBar } from '@/components/tasks/PrepFilterBar';
import { PrepCompletedToggle } from '@/components/tasks/PrepCompletedToggle';
import { TaskCategoryAccordion } from '@/components/tasks/TaskCategoryAccordion';
import { BOTTOM_NAV_HEIGHT_PX } from '@/components/nav/bottom-nav-constants';
import { toLocalISODate } from '@/utils/date-utils';

// FAB dimensions for dock math
const FAB_HEIGHT_PX = 56;
const FAB_MARGIN_PX = 16;

export function TaskListPage() {
  const { id: paramSeasonId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  // Route /prep/tasks?season=<id> carries the id in a query param;
  // route /seasons/:id/tasks carries it as a URL param. Support both.
  const urlSeasonId = paramSeasonId ?? searchParams.get('season') ?? undefined;
  const navigate = useNavigate();
  const { tasks, isLoading: tasksLoading, loadTasks, completeTask, uncompleteTask } = useTaskStore();
  const { getSeason, seasons, seasonsLoaded, isLoading: seasonsLoading, loadSeasons } = useSeasonStore();
  const { seasonId: storedSeasonId, setSeason: setStoredSeason } = usePrepActiveSeasonStore();

  const [activeFilter, setActiveFilter] = useState<PrepFilter>('all');

  const today = useMemo(() => new Date(), []);
  const todayStr = toLocalISODate(today);

  // Guard: seed the stored season only on first mount. After that, only explicit
  // user taps (via onSelect → setStoredSeason) should update the season.
  const hasSeededRef = useRef(false);

  useEffect(() => {
    if (seasons.length === 0) void loadSeasons();
  }, [seasons.length, loadSeasons]);

  // Seed the active season exactly once on first mount after seasons are loaded.
  // URL param wins if present; otherwise run the default-selection ladder.
  // Never re-seeds on subsequent renders — prevents reverting user's selector tap.
  useEffect(() => {
    if (hasSeededRef.current) return;
    if (!seasonsLoaded || seasons.length === 0) return;

    hasSeededRef.current = true;
    const seedId = urlSeasonId ?? pickDefaultSeason(seasons, today)?.id ?? null;
    if (seedId && !storedSeasonId) {
      setStoredSeason(seedId);
    } else if (urlSeasonId && urlSeasonId !== storedSeasonId) {
      // URL param takes precedence over whatever is stored, but only on first load.
      setStoredSeason(urlSeasonId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seasonsLoaded, seasons.length]);

  // Effective season: after first-mount seed, the store is the source of truth.
  // Before seeding, fall back to urlSeasonId so the page doesn't briefly show "not found".
  const effectiveSeasonId = storedSeasonId ?? urlSeasonId ?? undefined;

  useEffect(() => {
    if (effectiveSeasonId) void loadTasks(effectiveSeasonId);
  }, [effectiveSeasonId, loadTasks]);

  const season = effectiveSeasonId ? getSeason(effectiveSeasonId) : undefined;

  // Build title map for predecessor names (task.id → task.title)
  const taskMap = useMemo(
    () => new Map(tasks.map((t) => [t.id, t])),
    [tasks],
  );

  // Single-classification chip counts (G5 — derived from same classifier, never static)
  const filterCounts = useMemo(
    () => countPrepFilters(tasks, today),
    [tasks, today],
  );

  // Completion counts for the header — single-classification
  const completedCount = useMemo(
    () => tasks.filter((t) => deriveSingleStatus(t, todayStr) === 'done').length,
    [tasks, todayStr],
  );
  const totalCount = tasks.length;

  // Tasks filtered by chip — used for group display
  const filteredTasks = useMemo(
    () => filterOpenTasksForPrep(tasks, activeFilter, today),
    [tasks, activeFilter, today],
  );

  // Group by category (All chip shows all tasks; other chips filter first)
  // For the 'all' filter we show all tasks grouped; other filters show only matching tasks.
  const groupedTasks = useMemo(
    () => groupByCategory(filteredTasks),
    [filteredTasks],
  );

  // Initial expand-state per category, DERIVED (not effect-seeded) — this removes the
  // setState-in-effect cascade flagged by react-hooks/set-state-in-effect. Keyed on the
  // task-set *identity* (the `tasks` reference), NOT `tasks.length`: loadTasks() returns a
  // fresh array per season, so switching to a different season re-derives a fresh initial
  // state even when two seasons happen to have the same task count. Computed from ALL tasks
  // (not the filtered set). User toggles after mount live inside TaskCategoryAccordion, so
  // this only supplies each accordion's initialExpanded value.
  const expandStates = useMemo<Map<TaskCategory, boolean>>(
    () => computeInitialExpandStates(groupByCategory(tasks), todayStr),
    [tasks, todayStr],
  );

  async function handleToggleComplete(task: Task) {
    if (task.completed) {
      await uncompleteTask(task.id);
    } else {
      await completeTask(task.id);
    }
  }

  // Done tasks for the completed footer toggle
  const doneTasks = useMemo(
    () => tasks.filter((t) => deriveSingleStatus(t, todayStr) === 'done'),
    [tasks, todayStr],
  );

  // While the season store is hydrating, show a skeleton rather than the "not found" error.
  if (!seasonsLoaded || (seasonsLoading && !season)) {
    return (
      <div className="flex flex-col gap-4 animate-pulse py-6 px-4">
        <div className="h-7 bg-[var(--color-elevated)] rounded w-48" />
        <div className="h-2 bg-[var(--color-elevated)] rounded w-full" />
        <div className="h-10 bg-[var(--color-elevated)] rounded w-full" />
        <div className="h-10 bg-[var(--color-elevated)] rounded w-full" />
      </div>
    );
  }

  // Seasons loaded but none exist — user hasn't created a season yet.
  if (!effectiveSeasonId) {
    return (
      <div data-testid="no-seasons-state">
        <OperationalGuide
          illustration={noSeasonsPng}
          heading="Set Up Your Season"
          subtext="Configure your season dates to align with state DNR regulations."
          ctaLabel="Add Season"
          ctaLink="/seasons/new"
          data-guide-id="task-list-no-seasons"
        />
      </div>
    );
  }

  // Explicit season ID given but not found in the store.
  if (!season && !tasksLoading) {
    return (
      <div className="flex flex-col gap-4 py-8">
        <p className="text-[var(--color-text-muted)] text-body-sm">Season not found.</p>
        <Link to="/seasons" className="text-body-sm text-[var(--color-accent)] hover:underline">
          Back to Seasons
        </Link>
      </div>
    );
  }

  const hasAnyTasks = totalCount > 0;
  const hasFilteredResults = groupedTasks.size > 0;

  // FAB dock math: bottom = BOTTOM_NAV_HEIGHT_PX + safe-area + margin
  // No hardcoded 56 in the FAB math — consumed from the constant.
  const fabBottom = `calc(env(safe-area-inset-bottom) + ${BOTTOM_NAV_HEIGHT_PX}px + ${FAB_MARGIN_PX}px)`;
  // List container bottom padding: FAB height + margin + safe area + nav height
  const listPaddingBottom = `calc(env(safe-area-inset-bottom) + ${BOTTOM_NAV_HEIGHT_PX}px + ${FAB_HEIGHT_PX}px + ${FAB_MARGIN_PX * 2}px)`;

  return (
    <div
      className="flex flex-col gap-0"
      data-testid="task-list-page"
      style={{ paddingBottom: hasAnyTasks ? listPaddingBottom : undefined }}
    >
      {/* Sticky instrument header with season switcher (G4) */}
      {season && (
        <PrepHeader
          season={season}
          seasons={seasons}
          totalCount={totalCount}
          completedCount={completedCount}
          onBack={() => navigate(`/seasons/${effectiveSeasonId}`)}
          onSeasonSelect={(id) => setStoredSeason(id)}
        />
      )}

      {/* Filter bar — reconciled chip counts from single classifier (G5) */}
      <PrepFilterBar
        activeFilter={activeFilter}
        counts={filterCounts}
        onChange={setActiveFilter}
      />

      {/* Task list */}
      {tasksLoading ? (
        <div className="px-4 py-8 text-center text-[var(--color-text-muted)] text-body-sm">
          Loading tasks…
        </div>
      ) : totalCount === 0 ? (
        <OperationalGuide
          illustration={noTasksPng}
          heading="Your Task List is Clear"
          subtext="Generate a checklist from a template to begin your seasonal prep."
          ctaLabel="Browse Templates"
          ctaLink="/templates"
          data-guide-id="task-list-empty"
        />
      ) : !hasFilteredResults ? (
        <div
          className="flex flex-col items-center justify-center text-center"
          data-testid="tasks-filtered-empty"
          style={{ padding: '40px 20px' }}
        >
          <p className="font-display text-[18px] text-[var(--color-text-display)]">Nothing here</p>
          <p className="text-body-sm text-[var(--color-text-muted)] mt-1">No tasks match this filter.</p>
        </div>
      ) : (
        <div className="bg-[var(--color-surface)]" data-testid="task-group-list">
          {Array.from(groupedTasks.entries()).map(([category, categoryTasks]) => {
            const initialExpanded = expandStates.get(category) ?? true;

            return (
              <TaskCategoryAccordion
                key={category}
                category={category}
                tasks={categoryTasks}
                seasonId={effectiveSeasonId}
                taskMap={taskMap}
                todayStr={todayStr}
                initialExpanded={initialExpanded}
                onToggleComplete={handleToggleComplete}
              />
            );
          })}
        </div>
      )}

      {/* Completed footer toggle */}
      {activeFilter === 'all' && (
        <PrepCompletedToggle
          doneTasks={doneTasks}
          seasonId={effectiveSeasonId}
          onToggleComplete={handleToggleComplete}
        />
      )}

      {/* FAB — docked with proper safe-area-inset-bottom + nav height clearance (G6 / Step 6) */}
      {hasAnyTasks && (
        <button
          type="button"
          onClick={() => navigate(`/prep/tasks/new${effectiveSeasonId ? `?season=${effectiveSeasonId}` : ''}`)}
          aria-label="Create new task"
          data-testid="fab-create-task"
          data-fab-variant="ember"
          className="fixed right-4 w-14 h-14 rounded-full bg-[var(--color-accent)] text-[var(--color-text-display)] shadow-ember flex items-center justify-center hover:bg-[var(--color-accent-hover)] active:bg-[var(--color-accent-pressed)] transition-colors duration-100 z-20"
          style={{ bottom: fabBottom }}
        >
          <Plus size={24} />
        </button>
      )}
    </div>
  );
}
